import React from 'react';
import { ReminderItem } from '../../types';
import { Icons } from '../Icons';

interface ReminderQueueProps {
  reminders: ReminderItem[];
  onUpdateStatus?: (id: string, status: ReminderItem['status']) => void;
  onDelete?: (id: string) => void;
  isPatient?: boolean;
}

const statusStyle = (status: ReminderItem['status']) => {
  if (status === 'sent') return 'bg-emerald-100 text-emerald-700';
  if (status === 'error') return 'bg-rose-100 text-rose-700';
  return 'bg-amber-100 text-amber-700';
};

export const ReminderQueue: React.FC<ReminderQueueProps> = ({ reminders, onUpdateStatus, onDelete, isPatient = false }) => {
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-3xl font-black">{isPatient ? 'My Notifications' : 'Reminder Queue'}</h2>
          <p className="text-sm text-slate-500">
            {isPatient ? 'Your reminders and notifications.' : 'Track queued, sent, and failed reminders.'}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-3xl border shadow-sm overflow-hidden animate-scaleIn">
        <div className="p-6">
          {reminders.length === 0 ? (
            <div className="text-center py-12 text-slate-300">
              <Icons.Bell />
              <p className="font-bold mt-2">No reminders queued</p>
              <p className="text-sm">Use “Send reminder” from Follow-Up to add items here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 uppercase text-[11px]">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold">Patient</th>
                    <th className="text-left px-4 py-3 font-bold">Contact</th>
                    <th className="text-left px-4 py-3 font-bold">Created</th>
                    <th className="text-left px-4 py-3 font-bold">Status</th>
                    {!isPatient && <th className="text-left px-4 py-3 font-bold">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {reminders.map(item => (
                    <tr key={item.id} className="border-b last:border-b-0">
                      <td className="px-4 py-3 font-bold text-slate-800">{item.patientName}</td>
                      <td className="px-4 py-3 text-slate-600">{item.contact}</td>
                      <td className="px-4 py-3 text-slate-500">{new Date(item.createdAt).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-3 py-1 rounded-xl text-[11px] font-black ${statusStyle(item.status)}`}>
                          {item.status === 'queued' ? 'Queued' : item.status === 'sent' ? 'Sent' : 'Error'}
                        </span>
                      </td>
                      {!isPatient && (
                        <td className="px-4 py-3 space-x-2">
                          <button
                            onClick={() => onUpdateStatus?.(item.id, 'sent')}
                            className="px-3 py-1 rounded-lg border text-xs font-bold hover:bg-slate-50"
                          >
                            Mark sent
                          </button>
                          <button
                            onClick={() => onUpdateStatus?.(item.id, 'error')}
                            className="px-3 py-1 rounded-lg border text-xs font-bold hover:bg-slate-50"
                          >
                            Mark error
                          </button>
                          <button
                            onClick={() => onDelete?.(item.id)}
                            className="px-3 py-1 rounded-lg bg-red-100 text-red-700 text-xs font-bold hover:bg-red-200"
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
